import { useState, useEffect } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { db } from "../lib/firebase";
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import {
  getPlanByTier,
  canCreateCourse,
  canAddLesson,
  canAddStudent,
  canAddInstructor,
  canUploadFile,
  getStorageUsagePercentage,
  shouldShowStorageWarning,
  getUpgradeRecommendation,
  getAddonRecommendation,
} from "../config/subscriptionPlans";
import { UsageTracking } from "../types/payment";

export function useSubscriptionLimits() {
  const { user } = useAuthContext();
  const [usage, setUsage] = useState<UsageTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [userTier, setUserTier] = useState<string>("free");
  const [addonStorageGB, setAddonStorageGB] = useState(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchUsageData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();
        const tier = userData?.subscriptionTier || "free";
        setUserTier(tier);

        const coursesQuery = query(
          collection(db, "courses"),
          where("instructorId", "==", user.uid)
        );
        const coursesSnapshot = await getDocs(coursesQuery);
        const courseCount = coursesSnapshot.size;

        let totalVideoStorage = 0;
        let totalFileStorage = 0;
        let totalStudents = 0;

        for (const courseDoc of coursesSnapshot.docs) {
          const enrollmentsQuery = query(
            collection(db, "enrollments"),
            where("courseId", "==", courseDoc.id)
          );
          const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
          totalStudents += enrollmentsSnapshot.size;

          const mediaQuery = query(collection(db, "courses", courseDoc.id, "media"));
          const mediaSnapshot = await getDocs(mediaQuery);

          mediaSnapshot.forEach((mediaDoc) => {
            const mediaData = mediaDoc.data();
            const fileSizeMB = (mediaData.fileSize || 0) / (1024 * 1024);

            if (mediaData.type === "video") {
              totalVideoStorage += fileSizeMB;
            } else {
              totalFileStorage += fileSizeMB;
            }
          });
        }

        const instructorsQuery = query(
          collection(db, "users"),
          where("role", "==", "instructor"),
          where("organizationId", "==", user.uid)
        );
        const instructorsSnapshot = await getDocs(instructorsQuery);
        const instructorCount = instructorsSnapshot.size + 1;

        const subscriptionDoc = await getDoc(doc(db, "subscriptions", user.uid));
        const subscriptionData = subscriptionDoc.data();
        const addons = subscriptionData?.addons || [];
        const totalAddonStorage = addons.reduce(
          (sum: number, addon: any) => sum + (addon.storageGB || 0),
          0
        );
        setAddonStorageGB(totalAddonStorage);

        setUsage({
          videoStorageUsed: totalVideoStorage,
          fileStorageUsed: totalFileStorage,
          courseCount,
          studentCount: totalStudents,
          instructorCount,
          lastUpdated: new Date(),
        });

        // Also update the user's storageUsedBytes field for consistency
        const totalStorageBytes = (totalVideoStorage + totalFileStorage) * 1024 * 1024; // Convert MB to bytes
        await updateDoc(doc(db, "users", user.uid), {
          storageUsedBytes: totalStorageBytes
        });
      } catch (error) {
        console.error("Error fetching usage data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, [user]);

  const checkCanCreateCourse = () => {
    if (!usage) return { allowed: false, reason: "Loading..." };
    const allowed = canCreateCourse(userTier, usage.courseCount);
    return {
      allowed,
      reason: allowed ? undefined : "ถึงขีดจำกัดจำนวนคอร์สแล้ว",
      upgrade: allowed ? undefined : getUpgradeRecommendation(userTier),
    };
  };

  const checkCanAddLesson = (currentLessonCount: number) => {
    const allowed = canAddLesson(userTier, currentLessonCount);
    return {
      allowed,
      reason: allowed ? undefined : "ถึงขีดจำกัดจำนวนบทเรียนแล้ว",
      upgrade: allowed ? undefined : getUpgradeRecommendation(userTier),
    };
  };

  const checkCanAddStudent = () => {
    if (!usage) return { allowed: false, reason: "Loading..." };
    const allowed = canAddStudent(userTier, usage.studentCount);
    return {
      allowed,
      reason: allowed ? undefined : "ถึงขีดจำกัดจำนวนนักเรียนแล้ว",
      upgrade: allowed ? undefined : getUpgradeRecommendation(userTier),
    };
  };

  const checkCanAddInstructor = () => {
    if (!usage) return { allowed: false, reason: "Loading..." };
    const allowed = canAddInstructor(userTier, usage.instructorCount);
    return {
      allowed,
      reason: allowed ? undefined : "ถึงขีดจำกัดจำนวนผู้สอนแล้ว",
      upgrade: allowed ? undefined : getUpgradeRecommendation(userTier),
    };
  };

  const checkCanUploadFile = (fileSize: number, isVideo: boolean) => {
    if (!usage) return { canUpload: false, reason: "Loading..." };

    const result = canUploadFile(
      userTier,
      fileSize,
      isVideo,
      usage.videoStorageUsed,
      usage.fileStorageUsed,
      addonStorageGB
    );

    if (!result.canUpload) {
      const upgrade = getUpgradeRecommendation(userTier);
      const addon = getAddonRecommendation(
        userTier,
        usage.videoStorageUsed,
        usage.fileStorageUsed
      );

      return {
        ...result,
        upgrade,
        addon,
      };
    }

    return result;
  };

  const getVideoStorageInfo = () => {
    if (!usage) return null;

    const percentage = getStorageUsagePercentage(
      userTier,
      usage.videoStorageUsed,
      true,
      addonStorageGB
    );
    const showWarning = shouldShowStorageWarning(
      userTier,
      usage.videoStorageUsed,
      true,
      addonStorageGB
    );

    const plan = getPlanByTier(userTier);
    console.log('[getVideoStorageInfo] userTier:', userTier, 'plan:', plan);
    
    let totalStorage = 0;
    if (plan && plan.features.storageGB.videoStorage) {
      totalStorage = (plan.features.storageGB.videoStorage / 1024) + addonStorageGB;
    } else {
      // Fallback: use STORAGE_PLANS_GB from storagePlans.ts
      const STORAGE_PLANS_GB: Record<string, number> = {
        free: 5,
        pro_monthly: 20,
        pro_yearly: 25,        // 20 + 5 GB bonus
        pro: 20,               // Legacy support (treat as monthly)
        yearly: 25,            // Legacy support (treat as yearly with bonus)
        pro_plus_monthly: 30,
        pro_plus_yearly: 35,   // 30 + 5 GB bonus
        pro_plus: 30,          // Legacy support (treat as monthly)
      };
      totalStorage = (STORAGE_PLANS_GB[userTier] || 5) + addonStorageGB;
      console.warn('[getVideoStorageInfo] Plan not found, using fallback:', totalStorage, 'GB');
    }

    return {
      used: usage.videoStorageUsed / 1024, // Convert MB to GB
      total: totalStorage,
      percentage,
      showWarning,
    };
  };

  const getFileStorageInfo = () => {
    if (!usage) return null;

    const percentage = getStorageUsagePercentage(
      userTier,
      usage.fileStorageUsed,
      false,
      addonStorageGB
    );
    const showWarning = shouldShowStorageWarning(
      userTier,
      usage.fileStorageUsed,
      false,
      addonStorageGB
    );

    const plan = getPlanByTier(userTier);
    console.log('[getFileStorageInfo] userTier:', userTier, 'plan:', plan);
    
    let totalStorage = 0;
    if (plan && plan.features.storageGB.fileStorage) {
      totalStorage = (plan.features.storageGB.fileStorage / 1024) + addonStorageGB;
    } else {
      // Fallback: use STORAGE_PLANS_GB from storagePlans.ts
      const STORAGE_PLANS_GB: Record<string, number> = {
        free: 5,
        pro_monthly: 20,
        pro_yearly: 25,        // 20 + 5 GB bonus
        pro: 20,               // Legacy support (treat as monthly)
        yearly: 25,            // Legacy support (treat as yearly with bonus)
        pro_plus_monthly: 30,
        pro_plus_yearly: 35,   // 30 + 5 GB bonus
        pro_plus: 30,          // Legacy support (treat as monthly)
      };
      totalStorage = (STORAGE_PLANS_GB[userTier] || 5) + addonStorageGB;
      console.warn('[getFileStorageInfo] Plan not found, using fallback:', totalStorage, 'GB');
    }

    return {
      used: usage.fileStorageUsed / 1024, // Convert MB to GB
      total: totalStorage,
      percentage,
      showWarning,
    };
  };

  const getPlanLimits = () => {
    const plan = getPlanByTier(userTier);
    return plan?.features || null;
  };

  return {
    usage,
    loading,
    userTier,
    addonStorageGB,
    checkCanCreateCourse,
    checkCanAddLesson,
    checkCanAddStudent,
    checkCanAddInstructor,
    checkCanUploadFile,
    getVideoStorageInfo,
    getFileStorageInfo,
    getPlanLimits,
    getUpgradeRecommendation: () => getUpgradeRecommendation(userTier),
    getAddonRecommendation: () =>
      usage
        ? getAddonRecommendation(userTier, usage.videoStorageUsed, usage.fileStorageUsed)
        : null,
  };
}
